<template>
    <Popover class="relative bg-logo-blue">
        <div class="pointer-events-none absolute inset-0 z-30 shadow" aria-hidden="true" />

        <div class="relative z-20">
            <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6 sm:py-2 md:justify-start md:space-x-10 lg:px-8">
                <div>
                    <Link href="/" class="flex">
                        <span class="sr-only">Top 5 Supplements</span>
                        <LogoWhite class="h-12 sm:h-16 sm:my-2" />
                    </Link>
                </div>

                <div class="-my-2 -mr-2 md:hidden">
                    <PopoverButton class="inline-flex items-center justify-center rounded-md bg-logo-blue p-2 text-white hover:text-gray-200 focus:outline-none">
                        <span class="sr-only">Open menu</span>
                        <Bars3Icon class="h-6 w-6" aria-hidden="true" />
                    </PopoverButton>
                </div>

                <div class="hidden md:flex md:flex-1 md:items-center md:justify-between">
                    <PopoverGroup as="nav" class="flex space-x-10">
                        <Popover v-slot="{ open }">
                            <PopoverButton :class="[open ? 'text-gray-300' : 'text-white', 'group inline-flex items-center rounded-md bg-logo-blue text-base font-medium hover:text-gray-200 focus:outline-none']">
                                <span>Articles</span>
                                <ChevronDownIcon :class="[open ? 'text-gray-300' : 'text-white', 'ml-2 h-5 w-5 group-hover:text-gray-200']" aria-hidden="true" />
                            </PopoverButton>

                            <transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 -translate-y-1" enter-to-class="opacity-100 translate-y-0" leave-active-class="transition ease-in duration-150" leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-1">
                                <PopoverPanel v-slot="{ close }" class="absolute inset-x-0 top-full z-10 hidden transform shadow-lg md:block">
                                    <div class="absolute inset-0 flex">
                                        <div class="w-1/2 bg-white" />
                                        <div class="w-1/2 bg-gray-50" />
                                    </div>

                                    <div class="relative bg-white mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
                                        <div class="relative px-4 py-8 sm:py-12 sm:px-6 lg:px-8 xl:pl-12">
                                            <h3 class="text-base font-medium text-gray-500">Topics</h3>

                                            <div class="mt-6 grid gap-6 sm:gap-8 lg:grid-cols-2">
                                                <Link v-for="tag in tags" :key="tag.id" :href="`/articles/tags/${tag.slug}`" class="-m-3 flex items-center rounded-lg p-3 transition duration-150 ease-in-out hover:bg-gray-50" @click="close()">
                                                    <div class="h-10 w-10 rounded-md sm:h-16 sm:w-16">
                                                        <img class="mx-auto w-full rounded-full" :src="imgSrc(tag, 'tag', '1x')" :srcset="imgSrcset(tag, 'tag')" :alt="tag.name">
                                                    </div>

                                                    <div class="ml-4">
                                                        <p class="text-base font-medium text-gray-900">{{ tag.name }}</p>
                                                    </div>
                                                </Link>
                                            </div>
                                        </div>

                                        <div class="bg-gray-50 px-4 py-8 sm:py-12 sm:px-6 lg:px-8 xl:pl-12">
                                            <div>
                                                <h3 class="text-base font-medium text-gray-500">Featured Articles</h3>
                                                <ul role="list" class="mt-6 space-y-6">
                                                    <li v-for="article in articles" :key="article.id" class="flow-root">
                                                        <Link :href="`/articles/${article.slug}`" class="-m-3 flex rounded-lg p-3 hover:bg-gray-100" @click="close()">
                                                            <div class="hidden flex-shrink-0 sm:block">
                                                                <img class="h-20 w-32 rounded-md object-cover" :src="imgSrc(article, 'article', '1x')" :srcSet="imgSrcset(article, 'article')" :alt="article.imageAlt" />
                                                            </div>

                                                            <div class="w-0 flex-1 sm:ml-8">
                                                                <h4 class="truncate text-base font-medium text-gray-900">{{ article.title }}</h4>
                                                                <p class="mt-1 text-sm text-gray-500">{{ article.description }}</p>
                                                            </div>
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </div>

                                            <div class="mt-6 text-sm font-medium">
                                                <Link href="/articles" class="flex items-center text-logo-blue hover:text-logo-blue-lighter" @click="close()">
                                                    View All Articles
                                                    <ArrowSmallRightIcon class="h-6 w-6" aria-hidden="true" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </PopoverPanel>
                            </transition>
                        </Popover>
                        <Link href="/about" class="text-base font-medium text-white hover:200">About Us</Link>
                    </PopoverGroup>
                </div>
            </div>
        </div>

        <transition enter-active-class="duration-200 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="duration-100 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
            <PopoverPanel v-slot="{ close }" focus class="absolute inset-x-0 top-0 z-50 origin-top-right transform p-2 transition md:hidden">
                <div class="divide-y-2 divide-gray-50 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                    <div class="px-5 pt-5 pb-6 sm:pb-8">
                        <div class="flex items-center justify-between">
                            <Link href="/" class="focus:outline-none" @click="close()">
                                <Logo class="h-12" />
                            </Link>

                            <div class="-mr-2">
                                <PopoverButton class="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none">
                                    <span class="sr-only">Close menu</span>
                                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                                </PopoverButton>
                            </div>
                        </div>

                        <div class="mt-6 sm:mt-8">
                            <nav>
                                <div class="grid gap-7 sm:grid-cols-2 sm:gap-y-8 sm:gap-x-4">
                                    <Link v-for="tag in tags" :key="tag.name" :href="`/articles/tags/${tag.slug}`" class="-m-3 flex items-center rounded-lg p-3 hover:bg-gray-50" @click="close()">
                                        <div class="h-10 w-10 rounded-md sm:h-16 sm:w-16">
                                            <img class="mx-auto w-full rounded-full" :src="imgSrc(tag, 'tag', '1x')" :srcset="imgSrcset(tag, 'tag')" :alt="tag.name">
                                        </div>

                                        <div class="ml-4 text-base font-medium text-gray-900">{{ tag.name }}</div>
                                    </Link>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>
            </PopoverPanel>
        </transition>
    </Popover>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import {
    Bars3Icon,
    XMarkIcon,
} from '@heroicons/vue/24/outline'
import {
    ArrowSmallRightIcon,
    ChevronDownIcon,
} from '@heroicons/vue/20/solid'
import {
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel
} from '@headlessui/vue'
import { useCdnAsset } from '../functions/useCdnAsset'
import LogoWhite from '../../svg/logo-white.svg'
import Logo from '../../svg/logo.svg'

export default {
    components: {
        Bars3Icon,
        XMarkIcon,
        ArrowSmallRightIcon,
        ChevronDownIcon,
        Link,
        Popover,
        PopoverButton,
        PopoverGroup,
        PopoverPanel,
        LogoWhite,
        Logo,
    },

    props: {
        articles: Object,
        tags: Object,
    },

    setup() {
        const { cdn } = useCdnAsset()

        function imgSrc(model, type, size) {
            const image = model.image.replace('@1x', `@${size}`)
            return cdn(`/images/${type}s/${image}`)
        }

        function imgSrcset(model, type) {
            return `${imgSrc(model, type, '1x')} 1x, ${imgSrc(model, type, '2x')} 2x`
        }

        return {
            imgSrcset,
            imgSrc,
        }
    }
}
</script>
